import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import apiClient, { uploadFormData } from '../utils/apiClient';
import { findPlatformId, getReleaseYear } from '../utils/gameApplicationPrefill';
import './Add.css';
import '../utils/loadingStyles.css';
import TagSelector from '../components/TagSelector';
import authClient from '../utils/authClient';
import ProgressModal from '../components/ProgressModal';
import GameSubmissionGuide from '../components/GameSubmissionGuide';

const VISITOR_DOCS_FLAG = 'rgmVisitorGameSubmissionDocsAcknowledged';
const VISITOR_SUBMISSION_TRACKERS = 'rgmVisitorGameSubmissionTrackers';
const VISITOR_ROM_LIMIT = 250 * 1024 * 1024;
const VISITOR_MANUAL_LIMIT = 30 * 1024 * 1024;

// List of available emulators in the system
const AVAILABLE_EMULATORS = [
  { value: 'ejs', label: 'EmulatorJS' },
  { value: 'ruffle', label: 'Ruffle (Flash)' },
  { value: 'apple2ts', label: 'Apple II TS' },
  { value: 'html', label: 'HTML (Web Games)' },
  { value: 'jsdos', label: 'JS-DOS (DOS Games)' },
  { value: 'emularity', label: 'Emularity (MAME)' },
  { value: 'encrusted', label: 'Encrusted' },
  { value: 'freej2me', label: 'FreeJ2ME (Java ME)' }
];

const Add = ({ visitorMode = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const prefillAttempted = useRef(false);
  const [isAuthenticated, setIsAuthenticated] = useState(visitorMode);
  const [contributorAlias, setContributorAlias] = useState('');
  const [showDocumentation, setShowDocumentation] = useState(true);
  const [documentationAcknowledged, setDocumentationAcknowledged] = useState(() => !visitorMode || localStorage.getItem(VISITOR_DOCS_FLAG) === '1');
  const [documentationAccepted, setDocumentationAccepted] = useState(false);
  const [prefillMessage, setPrefillMessage] = useState('');
  const [prefillError, setPrefillError] = useState('');
  
  // Check authentication status
  useEffect(() => {
    const checkAuth = () => {
      if (visitorMode) {
        setIsAuthenticated(true);
        return;
      }
      if (!authClient.isAuthenticated()) {
        // Redirect to login page with current location for redirect after login
        navigate('/login', { state: { from: { pathname: '/add' } } });
      } else {
        setIsAuthenticated(true);
      }
    };
    
    checkAuth();
  }, [navigate, visitorMode]);
  const [game, setGame] = useState({
    titleeng: '',
    titlechn: '',
    desc: '',
    additional_hint: '',
    platform: '',
    cover: '',
    manual: '',
    year: '',
    dev: '',
    publisher: '',
    translator: '',
    loadasname: '',
    launchoptions: '',
    multiplayer: 0,
    emulator: null, // Custom emulator override (null = use platform default)
    mobileoptimized: 1 // Default to 1 (yes/optimized)
  });

  // Add state for platforms
  const [platforms, setPlatforms] = useState([]);
  const [platformsLoading, setPlatformsLoading] = useState(true);

  // Add state for multiple manuals
  const [manuals, setManuals] = useState([]);

  const [files, setFiles] = useState({
    cover: null
  });

  // ROM Management State
  const [romMode, setRomMode] = useState('single'); // 'single' or 'multiple'
  const [roms, setRoms] = useState([
    { type: 'default', customType: '', file: null, fileName: '', fileSize: '' }
  ]);

  // Add state for manual files with index
  const [manualFiles, setManualFiles] = useState({});

  const [fileNames, setFileNames] = useState({
    cover: ''
  });

  // Add state for manual filenames with index
  const [manualFileNames, setManualFileNames] = useState({});

  const [fileSizes, setFileSizes] = useState({
    cover: ''
  });

  // Add state for manual file sizes with index
  const [manualFileSizes, setManualFileSizes] = useState({});

  // Add a state for the compress option
  const [compressCover, setCompressCover] = useState(false);
  // Add loading and error states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [loading, setLoading] = useState(false);

  // Progress modal states
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [currentStep, setCurrentStep] = useState(1);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [currentFileName, setCurrentFileName] = useState('');
  const [modalError, setModalError] = useState(null);

  // Define upload steps - reflecting actual process
  const uploadSteps = [
    {
      title: '验证数据',
      description: '检查表单数据和文件完整性'
    },
    {
      title: '准备上传',
      description: '打包所有文件和数据准备上传'
    },
    {
      title: '上传到服务器',
      description: '批量上传所有文件（封面、ROM、手册）到服务器'
    },
    {
      title: '完成',
      description: visitorMode ? '提交成功，正在跳转...' : '游戏添加成功，正在跳转...'
    }
  ];

  // Add selectedTagIds to state variables at the start of the component
  const [selectedTagIds, setSelectedTagIds] = useState([]);

  // Add state for custom emulator checkbox
  const [useCustomEmulator, setUseCustomEmulator] = useState(false);

  // ROM types available
  const romTypeOptions = [
    { value: 'default', label: '默认' },
    { value: 'europe', label: '欧版' },
    { value: 'usa', label: '美版' },
    { value: 'jpn', label: '日版' },
    { value: 'chn', label: '简中版' },
    { value: 'tchn', label: '繁中版' },
    { value: 'custom', label: '自定义' }
  ];

  // 加载平台
  useEffect(() => {
    const fetchPlatforms = async () => {
      try {
        setLoading(true);
        setPlatformsLoading(true);
        const response = await apiClient.get('/platforms');
        setPlatforms(response.data);
      } catch (err) {
        console.error('获取平台数据失败:', err);
        setSubmitError('加载平台数据失败，请刷新页面重试');
      } finally {
        setLoading(false);
        setPlatformsLoading(false);
      }
    };

    fetchPlatforms();
  }, []);

  // Prefill the form when opened from a game application in the manage page.
  useEffect(() => {
    const applicationId = new URLSearchParams(location.search).get('fromApplication');
    if (!applicationId || platformsLoading || prefillAttempted.current) return;

    prefillAttempted.current = true;

    const loadApplication = async () => {
      try {
        const response = await apiClient.get(`/game-applications/${encodeURIComponent(applicationId)}`);
        const application = response.data;
        const platformId = findPlatformId(platforms, application.platform);
        const posterUrl = application.poster
          ? new URL(application.poster, window.location.origin).toString()
          : '';

        setGame((previous) => ({
          ...previous,
          titleeng: application.title_en || '',
          titlechn: application.title_cn || '',
          desc: application.description || '',
          year: getReleaseYear(application.release_date),
          platform: platformId,
          cover: posterUrl,
          dev: application.developer || '',
          publisher: application.publisher || ''
        }));

        if (posterUrl) {
          try {
            const posterResponse = await fetch(posterUrl, { credentials: 'same-origin' });
            if (!posterResponse.ok) {
              throw new Error(`封面下载失败 (${posterResponse.status})`);
            }

            const blob = await posterResponse.blob();
            if (!blob.type.startsWith('image/')) {
              throw new Error('封面地址没有返回图片');
            }

            const extension = blob.type.split('/')[1]?.replace('jpeg', 'jpg') || 'jpg';
            const coverFile = new File(
              [blob],
              `application-${applicationId}-cover.${extension}`,
              { type: blob.type }
            );
            setFiles((previous) => ({ ...previous, cover: coverFile }));
            setFileNames((previous) => ({ ...previous, cover: coverFile.name }));
            setFileSizes((previous) => ({ ...previous, cover: formatFileSize(coverFile.size) }));
          } catch (coverError) {
            console.error('Error importing application cover:', coverError);
            setPrefillError('其他信息已填写，但封面无法自动导入。请在提交前手动上传封面图片。');
          }
        }

        if (!platformId && application.platform) {
          setPrefillError((previous) =>
            [previous, `未找到与“${application.platform}”对应的平台，请手动选择。`]
              .filter(Boolean)
              .join(' ')
          );
        }

        setPrefillMessage(visitorMode
          ? `正在协助处理游戏申请 #${applicationId}，资料已自动填写。`
          : `已从游戏申请 #${applicationId} 填写资料。`);
      } catch (error) {
        console.error('Error loading game application for prefill:', error);
        setPrefillError('无法读取游戏申请资料，请返回管理页后重试。');
      }
    };

    loadApplication();
  }, [location.search, platforms, platformsLoading, visitorMode]);

  // Function to format file size
  const formatFileSize = (bytes) => {
    if (bytes === 0) return '0 B';
    const sizes = ['B', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(1024));
    return parseFloat((bytes / Math.pow(1024, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleChange = (e) => {
    setGame(prev => ({ ...prev, [e.target.name]: e.target.value }));
  };

  // Add a handler for checkbox changes
  const handleCheckboxChange = (e) => {
    setGame(prev => ({ ...prev, [e.target.name]: e.target.checked ? 1 : 0 }));
  };

  // Handler for custom emulator checkbox
  const handleCustomEmulatorChange = (e) => {
    const checked = e.target.checked;
    setUseCustomEmulator(checked);
    if (!checked) {
      // Reset emulator to null when unchecked
      setGame(prev => ({ ...prev, emulator: null }));
    }
  };

  const handleFileChange = (e, type) => {
    const file = e.target.files[0];
    if (file) {
      setFiles(prev => ({ ...prev, [type]: file }));
      setFileNames(prev => ({ ...prev, [type]: file.name }));
      setFileSizes(prev => ({ ...prev, [type]: formatFileSize(file.size) }));
      // Create a temporary URL for preview
      const url = URL.createObjectURL(file);
      setGame(prev => ({ ...prev, [type]: url }));
    }
  };

  // Handle ROM file changes
  const handleRomFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (visitorMode && file.size > VISITOR_ROM_LIMIT) {
        e.target.value = '';
        setSubmitError('ROM 文件不得超过 250 MB。');
        return;
      }
      setRoms(prev => prev.map((rom, i) => 
        i === index 
          ? { ...rom, file, fileName: file.name, fileSize: formatFileSize(file.size) } 
          : rom
      ));
    }
  };

  // Handle ROM type selection
  const handleRomTypeChange = (e, index) => {
    const type = e.target.value;
    setRoms(prev => prev.map((rom, i) => 
      i === index ? { ...rom, type } : rom
    ));
  };

  // Handle custom ROM type input
  const handleCustomTypeChange = (e, index) => {
    const customType = e.target.value;
    setRoms(prev => prev.map((rom, i) => 
      i === index ? { ...rom, customType } : rom
    ));
  };

  // Add a new ROM entry
  const addRom = () => {
    setRoms(prev => [...prev, { type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
  };

  // Remove a ROM entry
  const removeRom = (index) => {
    if (roms.length > 1) {
      setRoms(roms.filter((_, i) => i !== index));
    }
  };

  // Switch between single and multiple ROM modes
  const handleRomModeChange = (mode) => {
    setRomMode(mode);
    if (mode === 'single') {
      setRoms([{ type: 'default', customType: '', file: null, fileName: '', fileSize: '' }]);
    }
  };

  // Handle manual file changes with index
  const handleManualFileChange = (e, index) => {
    const file = e.target.files[0];
    if (file) {
      if (visitorMode && file.size > VISITOR_MANUAL_LIMIT) {
        e.target.value = '';
        setSubmitError('手册文件不得超过 30 MB。');
        return;
      }
      setManualFiles(prev => ({ ...prev, [index]: file }));
      setManualFileNames(prev => ({ ...prev, [index]: file.name }));
      setManualFileSizes(prev => ({ ...prev, [index]: formatFileSize(file.size) }));
      
      // Update the manual's temporary preview URL
      setManuals(prev => prev.map((manual, i) => 
        i === index 
          ? { ...manual, tempUrl: URL.createObjectURL(file) } 
          : manual
      ));
    }
  };

  // Function to add a new manual
  const addManual = () => {
    setManuals(prev => [...prev, { name: `手册${prev.length + 1}`, path: null }]);
  };

  // Function to remove a manual
  const removeManual = (index) => {
    // Check if this manual has a file being uploaded
    if (manualFiles[index]) {
      URL.revokeObjectURL(manuals[index].tempUrl);
      
      // Remove the file from the state
      const updatedFiles = { ...manualFiles };
      delete updatedFiles[index];
      setManualFiles(updatedFiles);
      
      const updatedFileNames = { ...manualFileNames };
      delete updatedFileNames[index];
      setManualFileNames(updatedFileNames);
      
      const updatedFileSizes = { ...manualFileSizes };
      delete updatedFileSizes[index];
      setManualFileSizes(updatedFileSizes);
    }
    
    // Remove the manual from the array
    setManuals(prev => prev.filter((_, i) => i !== index));
  };

  // Function to update manual name
  const updateManualName = (index, newName) => {
    setManuals(prev => prev.map((manual, i) => 
      i === index ? { ...manual, name: newName } : manual
    ));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (visitorMode && !contributorAlias.trim()) {
      setSubmitError('请填写贡献者姓名或别名。');
      return;
    }
    
    // Validate ROM is present since it's required by the database
    if (roms.length === 0 || !roms.some(rom => rom.file)) {
      setModalError("至少需要一个ROM文件。请上传ROM文件后再试。");
      setShowProgressModal(true);
      return;
    }

    if (visitorMode && !documentationAcknowledged) {
      setSubmitError('请先阅读并确认左侧的添加游戏指南。');
      return;
    }

    if (manuals.length > 1 && manuals.some(manual => !manual.name.trim())) {
      setSubmitError('上传多个手册时，请为每个手册填写名称。');
      return;
    }
    
    setIsSubmitting(true);
    setSubmitError(null);
    setModalError(null);
    setShowProgressModal(true);
    setCurrentStep(1);
    setUploadProgress(0);
    setCurrentFileName('');
    
    try {
      // Step 1: Validate data
      setCurrentStep(1);
      setCurrentFileName('检查必需文件...');
      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Get current user ID
      const currentUser = visitorMode ? null : authClient.getCurrentUser();
      
      // Step 2: Prepare upload
      setCurrentStep(2);
      setCurrentFileName('准备上传数据包...');
      
      // Create FormData object
      const formData = new FormData();
      
      // Add admin ID if user is logged in
      if (currentUser && currentUser.id) {
        formData.append('adminId', currentUser.id);
      }
      if (visitorMode) {
        formData.append('contributorAlias', contributorAlias.trim());
        formData.append('documentationAcknowledged', localStorage.getItem(VISITOR_DOCS_FLAG) === '1' ? '1' : '0');
        const applicationId = new URLSearchParams(location.search).get('fromApplication');
        if (applicationId) formData.append('gameApplicationId', applicationId);
      }
      
      // Add text fields
      Object.keys(game).forEach(key => {
        if (game[key] && key !== 'cover' && key !== 'manual' && key !== 'rom' && key !== 'jpnrom' && key !== 'chnrom' && key !== 'emulator') {
          formData.append(key, game[key]);
        }
      });
      
      // Add emulator field only if custom emulator is selected
      if (useCustomEmulator && game.emulator) {
        formData.append('emulator', game.emulator);
      }

      // Add compress option
      formData.append('compressed', compressCover ? '1' : '0');

      // Convert manuals array to JSON and add to form data
      const manualsToUpload = [...manuals].map(({ tempUrl, fileSize, ...manual }, index) => ({
        ...manual,
        name: manual.name.trim() || `手册${index + 1}`
      }));
      if (manualsToUpload.length > 0) {
        formData.append('manual', JSON.stringify(manualsToUpload));
      }

      // Add cover file to batch
      if (files.cover) {
        setCurrentFileName(`添加封面: ${files.cover.name}`);
        // Check file size
        if (files.cover.size > 500 * 1024 * 1024) {
          throw new Error(`Cover image is too large. Maximum size is 500MB.`);
        }
        formData.append('cover', files.cover);
      }

      // Add ROM files to batch
      if (romMode === 'single') {
        // For single ROM mode, just add the file without type information
        if (roms[0].file) {
          setCurrentFileName(`添加ROM: ${roms[0].file.name}`);
          if (roms[0].file.size > (visitorMode ? VISITOR_ROM_LIMIT : 500 * 1024 * 1024)) {
            throw new Error(visitorMode ? 'ROM 文件不得超过 250 MB。' : 'ROM file is too large. Maximum size is 500MB.');
          }
          formData.append('rom', roms[0].file);
        }
      } else {
        // For multiple ROM mode
        const romTypes = [];
        const customRomTypes = {};
        
        // Add each ROM file
        roms.forEach((rom, index) => {
          if (rom.file) {
            setCurrentFileName(`添加ROM ${index + 1}: ${rom.file.name}`);
            if (rom.file.size > (visitorMode ? VISITOR_ROM_LIMIT : 500 * 1024 * 1024)) {
              throw new Error(visitorMode ? `第 ${index + 1} 个 ROM 文件不得超过 250 MB。` : `ROM file ${index + 1} is too large. Maximum size is 500MB.`);
            }
            formData.append('rom', rom.file);
            romTypes.push(rom.type);
            
            // Store custom type if applicable
            if (rom.type === 'custom') {
              customRomTypes[index] = rom.customType || `自定义${index + 1}`;
            }
          }
        });
        
        // Add ROM types and custom types information
        formData.append('romTypes', JSON.stringify(romTypes));
        formData.append('customRomTypes', JSON.stringify(customRomTypes));
      }

      // Add manual files to batch
      if (Object.keys(manualFiles).length > 0) {
        Object.keys(manualFiles).forEach(index => {
          if (manualFiles[index]) {
            setCurrentFileName(`添加手册: ${manualFiles[index].name}`);
            // Check file size
            if (manualFiles[index].size > (visitorMode ? VISITOR_MANUAL_LIMIT : 500 * 1024 * 1024)) {
              throw new Error(visitorMode ? '手册文件不得超过 30 MB。' : 'Manual file is too large. Maximum size is 500MB.');
            }
            formData.append(`manual_${index}`, manualFiles[index]);
          }
        });
      }

      // Add selected tag IDs to the form data
      console.log('About to submit with selectedTagIds:', selectedTagIds);
      if (selectedTagIds.length > 0) {
        console.log('Adding tagIds to formData:', JSON.stringify(selectedTagIds));
        formData.append('tagIds', JSON.stringify(selectedTagIds));
      } else {
        console.log('No tags selected, not adding tagIds to formData');
      }

      console.log('Submitting form data with ROMs:', {
        romMode,
        roms: roms.map(rom => ({ type: rom.type, fileName: rom.fileName, hasFile: !!rom.file })),
        manuals: manualsToUpload,
        manualFiles: Object.keys(manualFiles),
        hasCoverFile: !!files.cover,
        selectedTagIds: selectedTagIds
      });

      await new Promise(resolve => setTimeout(resolve, 300));
      
      // Step 3: Upload to server (this is where the real upload happens)
      setCurrentStep(3);
      setCurrentFileName('正在上传所有文件到服务器...');
      
      // Use our centralized uploadFormData function with progress tracking
      const data = await uploadFormData(visitorMode ? '/visitor-game-submissions' : '/games', formData, 'POST', (progress) => {
        setUploadProgress(progress);
        if (progress < 100) {
          setCurrentFileName(`上传中... ${progress}%`);
        }
      });
      console.log('Server response:', data);
      
      // Step 4: Complete
      setCurrentStep(4);
      setCurrentFileName('上传完成');
      setUploadProgress(100);
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Hide progress modal and navigate
      setShowProgressModal(false);
      
      if (visitorMode) {
        try {
          const existing = JSON.parse(localStorage.getItem(VISITOR_SUBMISSION_TRACKERS) || '[]');
          const trackers = Array.isArray(existing) ? existing : [];
          const next = [
            { id: data.id, token: data.trackingToken },
            ...trackers.filter((item) => item?.token !== data.trackingToken)
          ].slice(0, 100);
          localStorage.setItem(VISITOR_SUBMISSION_TRACKERS, JSON.stringify(next));
        } catch (storageError) {
          console.error('Unable to store submission tracker:', storageError);
        }
        navigate(`/contribute-games/submitted/${data.id}`, { replace: true, state: { message: data.message } });
      } else {
        // Use timeout to ensure server has processed the new game before navigating.
        setTimeout(() => navigate('/manage'), 500);
      }
    } catch (err) {
      console.error('Error submitting form:', err);
      
      // Show error in the progress modal instead of hiding it immediately
      setCurrentFileName(`错误: ${err.message}`);
      setUploadProgress(0);
      
      // Wait a moment for user to see the error, then hide modal and show form error
      setTimeout(() => {
        const contact = err.responseData?.contact;
        const contactText = contact ? ` 微信：${contact.wechat}；邮箱：${contact.email}` : '';
        setSubmitError(`${err.message || 'Unknown error. Please try again or contact support.'}${contactText}`);
        setShowProgressModal(false);
      }, 2000);
    } finally {
      setIsSubmitting(false);
    }
  };

  // Replace the platform text input with a select dropdown
  const renderPlatformSelect = () => {
    if (platformsLoading) {
      return <div className="loading-field">Loading platforms...</div>;
    }
    
    return (
      <select
        name="platform"
        value={game.platform}
        onChange={handleChange}
        required
        className="emulator-select"
      >
        <option value="">-- 选择平台 --</option>
        {platforms.map(platform => (
          <option key={platform.id} value={platform.id}>
            {platform.nameeng} {platform.namechn ? `(${platform.namechn})` : ''}
          </option>
        ))}
      </select>
    );
  };

  // Only render the form if user is authenticated
  if (!isAuthenticated) {
    return <div className="loading-container"><div className="loading"></div></div>;
  }
  
  return (
    <>
      <ProgressModal
        isVisible={showProgressModal}
        currentStep={currentStep}
        progress={uploadProgress}
        fileName={currentFileName}
        totalSteps={uploadSteps.length}
        steps={uploadSteps}
        isError={!!modalError}
        errorMessage={modalError}
        onClose={() => {
          setShowProgressModal(false);
          setModalError(null);
        }}
      />
      <div className={visitorMode ? 'visitor-add-layout' : ''}>
      {visitorMode && (
        <aside className={`submission-guide-panel ${showDocumentation ? 'open' : ''}`} aria-labelledby="submission-docs-title">
          <div className="submission-guide-heading">
            <div><span>提交指南</span><h2 id="submission-docs-title">添加游戏前请阅读</h2></div>
            <button type="button" onClick={() => setShowDocumentation(false)} aria-label="收起指南">×</button>
          </div>
          <div className="submission-docs-scroll"><GameSubmissionGuide /></div>
          {!documentationAcknowledged && (
            <div className="submission-guide-confirm">
              <label className="submission-docs-acknowledgement">
                <input type="checkbox" checked={documentationAccepted} onChange={(event) => setDocumentationAccepted(event.target.checked)} />
                我已阅读并理解以上添加游戏指南
              </label>
              <button
                type="button"
                className="submit-btn"
                disabled={!documentationAccepted}
                onClick={() => {
                  localStorage.setItem(VISITOR_DOCS_FLAG, '1');
                  setDocumentationAcknowledged(true);
                }}
              >确认并开始填写</button>
            </div>
          )}
        </aside>
      )}
      <div className="add-container">
      <h1>{visitorMode ? '提交新游戏' : '添加新游戏'}</h1>
      {visitorMode && (
        <button type="button" className="documentation-button" onClick={() => setShowDocumentation(true)}>查看添加游戏指南</button>
      )}
      {prefillMessage && <div className="prefill-message">{prefillMessage}</div>}
      {prefillError && <div className="prefill-warning">{prefillError}</div>}
      {submitError && (
        <div className="error-message">
          <p>{submitError}</p>
        </div>
      )}
      {loading && <div className="loading-message">加载中...</div>}
      
      <form onSubmit={handleSubmit} noValidate className={visitorMode && !documentationAcknowledged ? 'submission-form-locked' : ''}>
        {visitorMode && !documentationAcknowledged && <p className="submission-form-lock-note">阅读并确认左侧指南后即可填写。</p>}
        {visitorMode && (
          <div className="form-group">
            <label>贡献者姓名 / 别名: *</label>
            <input
              type="text"
              value={contributorAlias}
              maxLength="100"
              onChange={(event) => setContributorAlias(event.target.value)}
              required
              placeholder="游戏发布后用于致谢的姓名或别名"
            />
          </div>
        )}
        <div className="form-group">
          <label>平台: *</label>
          {renderPlatformSelect()}
        </div>

        {/* Custom Emulator Section */}
        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              checked={useCustomEmulator}
              onChange={handleCustomEmulatorChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">使用非标准模拟器</span>
          </label>
          <div className="field-hint">
            勾选此项以覆盖平台默认模拟器设置
          </div>
        </div>

        {useCustomEmulator && (
          <div className="form-group">
            <label>自定义模拟器: *</label>
            <select
              name="emulator"
              value={game.emulator || ''}
              onChange={handleChange}
              required
              className="emulator-select"
            >
              <option value="">-- 选择模拟器 --</option>
              {AVAILABLE_EMULATORS.map(emulator => (
                <option key={emulator.value} value={emulator.value}>
                  {emulator.label}
                </option>
              ))}
            </select>
            <div className="field-hint">
              选择此游戏专用的模拟器，将覆盖平台默认设置
            </div>
          </div>
        )}

        <div className="form-group">
          <label>英文标题:</label>
          <input
            type="text"
            name="titleeng"
            value={game.titleeng}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>中文标题:</label>
          <input
            type="text"
            name="titlechn"
            value={game.titlechn}
            onChange={handleChange}
          />
        </div>

        <div className="form-group">
          <label>描述:</label>
          <textarea
            name="desc"
            value={game.desc}
            onChange={handleChange}
            rows="4"
          />
        </div>

        <div className="form-group">
          <label>额外提示 (显示在控制说明上方):</label>
          <textarea
            name="additional_hint"
            value={game.additional_hint}
            onChange={handleChange}
            rows="3"
            placeholder="可选：添加游戏特定的操作提示或注意事项"
          />
        </div>

        <div className="form-group">
          <label>发行年份:</label>
          <input
            type="number"
            name="year"
            value={game.year}
            onChange={handleChange}
            placeholder="例如: 1998"
          />
        </div>

        <div className="form-group">
          <label>开发商:</label>
          <input
            type="text"
            name="dev"
            value={game.dev}
            onChange={handleChange}
            placeholder="例如: 任天堂"
          />
        </div>

        <div className="form-group">
          <label>发行商:</label>
          <input
            type="text"
            name="publisher"
            value={game.publisher}
            onChange={handleChange}
            placeholder="例如: 任天堂"
          />
        </div>

        <div className="form-group">
          <label>汉化组:</label>
          <input
            type="text"
            name="translator"
            value={game.translator}
            onChange={handleChange}
            placeholder="例如: 无名汉化组"
          />
          <div className="field-hint">
            对于中文ROM，请填写汉化组或翻译者名称
          </div>
        </div>

        <div className="form-group">
          <label>加载名称 (MAME游戏专用):</label>
          <input
            type="text"
            name="loadasname"
            value={game.loadasname}
            onChange={handleChange}
            placeholder="例如: sf2.zip"
          />
          <div className="field-hint">
            对于MAME游戏，请输入模拟器应使用的确切ROM文件名（包括扩展名）
          </div>
        </div>

        <div className="form-group">
          <label>启动选项:</label>
          <input
            type="text"
            name="launchoptions"
            value={game.launchoptions}
            onChange={handleChange}
            placeholder="游戏的可选参数"
          />
          <div className="field-hint">
            该游戏特定的可选参数（某些模拟器使用）
          </div>
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="multiplayer"
              id="multiplayer"
              checked={game.multiplayer === 1}
              onChange={handleCheckboxChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">多人游戏支持</span>
          </label>
          <div className="field-hint">
            如果游戏支持多人模式，请启用此选项（适用于EJS和JS-DOS模拟器）
          </div>
        </div>

        <div className="form-group checkbox-group">
          <label className="checkbox-container">
            <input
              type="checkbox"
              name="mobileoptimized"
              id="mobileoptimized"
              checked={game.mobileoptimized === 1}
              onChange={handleCheckboxChange}
              className="custom-checkbox"
            />
            <span className="checkmark"></span>
            <span className="checkbox-label">移动端优化</span>
          </label>
          <div className="field-hint">
            游戏是否针对移动设备优化（未优化的游戏将在移动端Feed中被过滤）
          </div>
        </div>

        <div className="form-group">
          <label>标签:</label>
          <TagSelector 
            onChange={(selectedTags) => {
              console.log('TagSelector onChange called with:', selectedTags);
              // Store selected tag IDs in component state
              const tagIds = selectedTags.map(tag => tag.id);
              console.log('Setting selectedTagIds to:', tagIds);
              setSelectedTagIds(tagIds);
            }}
          />
          <div className="field-hint">
            选择描述此游戏的标签（类型、特点等）
          </div>
        </div>

        <div className="form-group">
          <label>封面图片:</label>
          <div className="input-group">
            <input
              type="text"
              name="cover"
              value={game.cover}
              onChange={handleChange}
              placeholder="封面图片URL或Blob"
              readOnly
            />
            <div className="file-input">
              <label className="file-label">
                {fileNames.cover || '上传文件'}
                <input
                  type="file"
                  accept="image/*"
                  onChange={(e) => handleFileChange(e, 'cover')}
                />
              </label>
            </div>
          </div>
          {fileSizes.cover && <div className="file-size-info">{fileSizes.cover}</div>}
          <div className="compress-option">
            <label className="checkbox-container">
              <input
                type="checkbox"
                id="compress-cover"
                checked={compressCover}
                onChange={(e) => setCompressCover(e.target.checked)}
                className="custom-checkbox"
              />
              <span className="checkmark"></span>
              <span className="checkbox-label">压缩图片（使用TinyPNG）</span>
            </label>
          </div>
          {game.cover && (
            <div className="image-preview-container">
              <img src={game.cover} alt="封面预览" className="preview" />
            </div>
          )}
        </div>

        {/* ROM Management Section */}
        <div className="form-group rom-section">
          <label>ROM文件: *</label>
          
          <div className="rom-mode-selector">
            <div className="rom-mode-option">
              <label className={`radio-container ${romMode === 'single' ? 'checked' : ''}`}>
                <input
                  type="radio"
                  id="rom-mode-single"
                  checked={romMode === 'single'}
                  onChange={() => handleRomModeChange('single')}
                  className="custom-radio"
                />
                <span className="radio-label">单个ROM</span>
              </label>
            </div>
            <div className="rom-mode-option">
              <label className={`radio-container ${romMode === 'multiple' ? 'checked' : ''}`}>
                <input
                  type="radio"
                  id="rom-mode-multiple"
                  checked={romMode === 'multiple'}
                  onChange={() => handleRomModeChange('multiple')}
                  className="custom-radio"
                />
                <span className="radio-label">多ROM版本</span>
              </label>
            </div>
          </div>
          
          <div className="roms-container">
            {roms.map((rom, index) => (
              <div key={index} className="rom-item">
                {romMode === 'multiple' && (
                  <div className="rom-header">
                    <div className="rom-type-selector">
                      <select
                        value={rom.type}
                        onChange={(e) => handleRomTypeChange(e, index)}
                        className="rom-type-select"
                      >
                        {romTypeOptions.map(option => (
                          <option key={option.value} value={option.value}>
                            {option.label}
                          </option>
                        ))}
                      </select>
                      
                      {rom.type === 'custom' && (
                        <input
                          type="text"
                          value={rom.customType}
                          onChange={(e) => handleCustomTypeChange(e, index)}
                          placeholder="输入自定义类型"
                          className="rom-custom-type"
                        />
                      )}
                    </div>
                    
                    {roms.length > 1 && (
                      <button
                        type="button"
                        className="remove-rom-button"
                        onClick={() => removeRom(index)}
                      >
                        移除
                      </button>
                    )}
                  </div>
                )}
                
                <div className="rom-file-section">
                  <div className="input-group">
                    <div className="file-input">
                      <label className="file-label">
                        {rom.fileName || '上传ROM'}
                        <input
                          type="file"
                          onChange={(e) => handleRomFileChange(e, index)}
                          required={index === 0}
                        />
                      </label>
                    </div>
                  </div>
                  {rom.fileSize && <div className="file-size-info">{rom.fileSize}</div>}
                </div>
              </div>
            ))}
            
            {/* Add new ROM button (only in multiple mode) */}
            {romMode === 'multiple' && (
              <div className="add-rom-section">
                <button
                  type="button"
                  className="add-rom-button"
                  onClick={addRom}
                >
                  添加ROM版本
                </button>
              </div>
            )}
          </div>
          <div className="field-hint">
            {romMode === 'single' 
              ? "单个ROM将被保存为默认版本"
              : "多ROM模式允许上传不同区域或翻译版本的ROM文件"}
          </div>
        </div>

        {/* Multi-Manual Management Section */}
        <div className="form-group manuals-section">
          <label>游戏手册:</label>
          <div className="manuals-container">
            {manuals.length === 0 ? (
              <div className="no-manuals-message">尚未添加手册</div>
            ) : (
              manuals.map((manual, index) => (
                <div key={index} className="manual-item">
                  <div className="manual-header">
                    <input
                      type="text"
                      value={manual.name}
                      onChange={(e) => updateManualName(index, e.target.value)}
                      placeholder={manuals.length === 1 ? '可选，默认保存为手册1' : '手册名称（必填）'}
                      className="manual-name-input"
                      required={manuals.length > 1}
                    />
                  </div>
                  
                  <div className="manual-file-section">
                    <div className="input-group">
                      <div className="file-input">
                        <label className="file-label">
                          {manualFileNames[index] || '上传文件'}
                          <input
                            type="file"
                            accept=".pdf,.jpg,.jpeg,.png"
                            onChange={(e) => handleManualFileChange(e, index)}
                          />
                        </label>
                      </div>
                    </div>
                    {manualFileSizes[index] && <div className="file-size-info">{manualFileSizes[index]}</div>}
                    <button
                      type="button"
                      className="remove-manual-button"
                      onClick={() => removeManual(index)}
                    >
                      删除
                    </button>
                  </div>
                </div>
              ))
            )}
            
            {/* Add new manual section */}
            <div className="add-manual-section">
              <button
                type="button"
                className="add-manual-button"
                onClick={addManual}
              >
                添加手册
              </button>
            </div>
          </div>
        </div>

        <button 
          type="submit" 
          className="submit-btn"
          disabled={isSubmitting}
        >
          {isSubmitting ? '提交中...' : visitorMode ? '提交审核' : '添加游戏'}
        </button>
      </form>
    </div>
    </div>
    </>
  );
};

export default Add;
